import { useState } from "react";
import type { PricingVersion } from "@shared-core/data/pricing-admin-types";
import { Card, CardContent } from "../primitives/card";
import { Button } from "../primitives/button";
import { Badge } from "../primitives/badge";
import { Separator } from "../primitives/separator";
import {
  LiveVersionLine,
  SupersededList,
  liveVersion,
  supersededVersions,
} from "./version-history";
import { History } from "lucide-react";

// A clickable registry tile showing an entity's version lifecycle — the live
// version and an expandable, revertable history (no drafts). Shared by the
// price-groups and catalogues admin lists so the two stay identical.

export type RegistryScope =
  "shared" | "full_range" | { customerName: string } | { privateTo: string };

export function RegistryTile({
  title,
  scope,
  ownership,
  versions = [],
  onOpen,
}: {
  title: string;
  scope: RegistryScope;
  /** Who owns the entity's content, when it has an owner worth showing ("Managed in code"). */
  ownership?: string;
  versions?: PricingVersion[];
  onOpen: () => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const live = liveVersion(versions);
  const hasHistory = supersededVersions(versions).length > 0;
  const isShared = scope === "shared";

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="gap-0 py-4 cursor-pointer transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{title}</span>
              <Badge variant={isShared ? "secondary" : "outline"}>
                {isShared
                  ? "Shared"
                  : scope === "full_range"
                    ? "Full range · staff only"
                    : "privateTo" in scope
                      ? `Private · ${scope.privateTo}`
                      : `Customer · ${scope.customerName}`}
              </Badge>
              {ownership && <Badge variant="outline">{ownership}</Badge>}
            </div>
            {live && <LiveVersionLine version={live} />}
          </div>
          {hasHistory && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowHistory((h) => !h);
              }}
            >
              <History className="size-4" /> History
            </Button>
          )}
        </div>

        {showHistory && hasHistory && (
          <>
            <Separator />
            <SupersededList versions={versions} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
